import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaBullhorn, FaCalendarAlt, FaDownload, FaExternalLinkAlt, FaStar, FaInfoCircle, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';

const Announcements = () => {
  const [filter, setFilter] = useState('all');

  const announcements = [
    {
      id: 1,
      title: 'Parent-Teacher Conference Schedule',
      date: 'March 15, 2024',
      category: 'important',
      priority: 'high',
      description: 'Parent-teacher conferences will be held on March 25-26, 2024. Please schedule your appointment through the school portal. This is a mandatory meeting for all parents.',
      icon: FaInfoCircle,
      color: 'blue',
      hasAttachment: true,
      attachmentName: 'Conference_Schedule.pdf',
      link: '#'
    },
    {
      id: 2,
      title: 'School Holiday Notice - Spring Break',
      date: 'March 10, 2024',
      category: 'general',
      priority: 'medium',
      description: 'The school will be closed for Spring Break from April 1-5, 2024. Classes will resume on Monday, April 8, 2024. Please ensure all assignments are completed before the break.',
      icon: FaCalendarAlt,
      color: 'green',
      hasAttachment: false,
      link: null
    },
    {
      id: 3,
      title: 'New Library Resources Available',
      date: 'March 5, 2024',
      category: 'academic',
      priority: 'medium',
      description: 'We have added new digital resources to our library collection. Students can access these resources through the school portal. This includes e-books, research databases, and educational videos.',
      icon: FaCheckCircle,
      color: 'purple',
      hasAttachment: true,
      attachmentName: 'Library_Resources_Guide.pdf',
      link: '#'
    },
    {
      id: 4,
      title: 'Sports Team Tryouts - Spring Season',
      date: 'March 1, 2024',
      category: 'sports',
      priority: 'medium',
      description: 'Tryouts for spring sports teams will begin next week. All interested students must register online and attend the mandatory orientation session.',
      icon: FaExclamationTriangle,
      color: 'orange',
      hasAttachment: true,
      attachmentName: 'Sports_Tryout_Schedule.pdf',
      link: '#'
    },
    {
      id: 5,
      title: 'Annual Science Fair Registration',
      date: 'February 28, 2024',
      category: 'academic',
      priority: 'high',
      description: 'Registration for the Annual Science Fair is now open. All students in grades 6-12 are encouraged to participate. Projects must be registered by March 10th.',
      icon: FaStar,
      color: 'red',
      hasAttachment: true,
      attachmentName: 'Science_Fair_Guidelines.pdf',
      link: '#'
    },
    {
      id: 6,
      title: 'School Bus Route Updates',
      date: 'February 25, 2024',
      category: 'transportation',
      priority: 'medium',
      description: 'Updated bus routes for the spring semester are now available. Please check the new schedules and inform the transportation office of any changes needed.',
      icon: FaInfoCircle,
      color: 'teal',
      hasAttachment: true,
      attachmentName: 'Bus_Routes_Spring.pdf',
      link: '#'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Announcements', color: 'gray' },
    { id: 'important', name: 'Important', color: 'red' },
    { id: 'academic', name: 'Academic', color: 'blue' },
    { id: 'general', name: 'General', color: 'green' },
    { id: 'sports', name: 'Sports', color: 'orange' },
    { id: 'transportation', name: 'Transportation', color: 'teal' }
  ];

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <FaExclamationTriangle className="w-4 h-4 text-red-500" />;
      case 'medium':
        return <FaInfoCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return <FaCheckCircle className="w-4 h-4 text-green-500" />;
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      important: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      academic: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      general: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      sports: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      transportation: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200'
    };
    return colors[category] || colors.general;
  };

  const filteredAnnouncements = filter === 'all' 
    ? announcements 
    : announcements.filter(announcement => announcement.category === filter);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
      <Helmet>
        <title>Rasoha Academy | Announcements - Latest News & Updates</title>
        <meta name="description" content="Stay updated with Rasoha Academy's latest announcements, news, and important updates. Get the latest information about school events, academic updates, and community news." />
        <meta name="keywords" content="Rasoha Academy Announcements, School News, Academic Updates, School Events, Narok School News, Important Notices" />
        <meta property="og:title" content="Rasoha Academy | Announcements - Latest News & Updates" />
        <meta property="og:description" content="Stay updated with Rasoha Academy's latest announcements, news, and important updates." />
        <meta property="og:url" content="https://rasohaacademy.com/announcements" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-aos="fade-up">
          <div className="flex items-center justify-center mb-6">
            <FaBullhorn className="w-12 h-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">
              Announcements
            </h1>
          </div>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-90">
            Stay updated with the latest news and important announcements from our school
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center" data-aos="fade-up">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setFilter(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  filter === category.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Announcements List */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8" data-aos="fade-up">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {filter === 'all' ? 'All Announcements' : categories.find(c => c.id === filter)?.name}
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              Showing {filteredAnnouncements.length} announcement{filteredAnnouncements.length !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="space-y-6">
            {filteredAnnouncements.map((announcement, index) => (
              <div
                key={announcement.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-3">
                      <div className={`w-10 h-10 ${announcement.color === 'blue' ? 'bg-blue-600' : announcement.color === 'green' ? 'bg-green-600' : announcement.color === 'purple' ? 'bg-purple-600' : announcement.color === 'orange' ? 'bg-orange-600' : announcement.color === 'red' ? 'bg-red-600' : 'bg-teal-600'} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <announcement.icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                            {announcement.title}
                          </h3>
                          {getPriorityIcon(announcement.priority)}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center">
                            <FaCalendarAlt className="w-4 h-4 mr-1" />
                            {announcement.date}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(announcement.category)}`}>
                            {announcement.category.charAt(0).toUpperCase() + announcement.category.slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="mb-4">
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      {announcement.description}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-3">
                      {announcement.hasAttachment && (
                        <button className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200">
                          <FaDownload className="w-4 h-4 mr-1" />
                          <span className="text-sm">{announcement.attachmentName}</span>
                        </button>
                      )}
                      {announcement.link && (
                        <a
                          href={announcement.link}
                          className="flex items-center text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors duration-200"
                        >
                          <FaExternalLinkAlt className="w-4 h-4 mr-1" />
                          <span className="text-sm">View Details</span>
                        </a>
                      )}
                    </div>
                    <button className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200">
                      <FaStar className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredAnnouncements.length === 0 && (
            <div className="text-center py-12" data-aos="fade-up">
              <FaBullhorn className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                No announcements found
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                There are no announcements in this category at the moment.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-aos="fade-up">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Stay Informed
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Never miss an important announcement. Subscribe to our notification system.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              Subscribe to Notifications
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300 transform hover:scale-105">
              View Archive
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Announcements; 